import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Hash, Loader, Lock, Search, ShieldCheck } from 'lucide-react';
import { Modal } from './ui/Modal';
import OsScroll from './ui/OsScroll';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { useChatStore } from '../store/chatStore';
import { useNotificationStore } from '../store/notificationStore';
import chatService from '../services/chatService';
import { ensureDm } from '../utils/chatOpen';
import { getChannelE2eKeys } from '../services/vaultShareService';
import {
  ensureE2eKeypair,
  encryptForRecipients,
  verifyRecipientKeys,
  pinRecipientKeys,
  KEY_CHECK,
} from '../utils/vaultShareCrypto';
import { channelDisplay, dmOther, initials, avatarColor, isCompleteEmail } from './chatHelpers';
import UserAvatar from './ui/UserAvatar';
import { setActiveTab } from '../store/uiModalStore';
import { VAULT_TYPE_META } from './VaultItemModal';
import './Chat.css';
import './Vault.css';

// A group larger than this would balloon the per-recipient key map past the
// 16 KB metadata cap (each entry carries an ephemeral JWK + wrapped key).
const MAX_RECIPIENTS = 40;
// The server enforces a 16 KB (16_000-byte) cap on chat message metadata
// (message.ex validate_metadata_size). A big item (e.g. a multi-KB SFTP PEM)
// wrapped to many recipients can exceed it, so guard on the real serialized
// size before sending rather than only on recipient count.
const MAX_METADATA_BYTES = 16000;

function newUid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * "Share in chat" for a Secrets-Vault item: pick a conversation (or start a
 * DM by exact email), optionally set an expiry, and send the item as an
 * E2E-encrypted `kind:"secret"` card. The item is encrypted client-side to
 * each member's published P-256 key (sender included); members who never
 * published a key can't receive and are surfaced before sending.
 *
 * `item` is a DECRYPTED vault item ({ ...envelope, data }).
 */
export default function VaultShareModal({ isOpen, onClose, item }) {
  const { t } = useTranslation();
  const channels = useChatStore((s) => s.channels);
  const currentUserId = useChatStore((s) => s.currentUserId);
  const setActiveChannel = useChatStore((s) => s.setActiveChannel);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState(null); // { channelId, name }
  // 0 = no expiry; otherwise hours until expires_at.
  const [expiryHours, setExpiryHours] = useState(0);
  // Members without a published key: { channel, keysMap, missingNames } —
  // sending pauses on a warning until "Share anyway" (or is blocked outright
  // when no other member can receive).
  const [pendingWarn, setPendingWarn] = useState(null);
  // A recipient's published key differs from the one we pinned on first use
  // (possible silent key substitution): { channel, keysMap, changedUserIds,
  // changedNames } — sending pauses until the user explicitly trusts the new
  // key.
  const [pendingKeyChange, setPendingKeyChange] = useState(null);
  // The trust-on-first-use check could not be RUN (vault locked, pin store
  // unreadable/unwritable, unreadable directory key): { channel, keysMap,
  // reason }. Distinct from "nothing changed" on purpose — there is no
  // "share anyway" here, because we cannot say whether anything changed.
  const [pendingVerifyFail, setPendingVerifyFail] = useState(null);
  const debounce = useRef(null);

  const EXPIRY_OPTIONS = [
    { value: 0, label: t('vaultShare.expiryNever', 'No expiry') },
    { value: 1, label: t('vaultShare.expiry1h', '1 hour') },
    { value: 24, label: t('vaultShare.expiry24h', '24 hours') },
    { value: 168, label: t('vaultShare.expiry7d', '7 days') },
  ];

  // Keyed on the item IDENTITY (id), not the whole object: VaultItemDetail
  // hands us a freshly-spread { ...envelope, data } and any re-render (e.g. a
  // vault:changed refresh triggered by our own first-share key publish) would
  // otherwise re-run this reset and wipe in-flight send state.
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setResults([]);
    setError('');
    setSending(false);
    setSentTo(null);
    setExpiryHours(0);
    setPendingWarn(null);
    setPendingKeyChange(null);
    setPendingVerifyFail(null);
  }, [isOpen, item?.id]);

  // People lookup is exact-email only (mirrors ShareToChatModal / the server
  // rule): the lookup fires only once a complete address is typed.
  useEffect(() => {
    if (!isOpen) return undefined;
    clearTimeout(debounce.current);
    if (!isCompleteEmail(query)) {
      setResults([]);
      return undefined;
    }
    debounce.current = setTimeout(() => {
      chatService.searchUsers(query.trim()).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(debounce.current);
  }, [query, isOpen]);

  const sortedChannels = useMemo(
    () => [...channels].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)),
    [channels]
  );
  const filteredChannels = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedChannels;
    return sortedChannels.filter((c) =>
      channelDisplay(c, currentUserId).name.toLowerCase().includes(q)
    );
  }, [sortedChannels, query, currentUserId]);

  if (!item) return null;

  const typeMeta = VAULT_TYPE_META[item.type] || VAULT_TYPE_META.custom;
  const TypeIcon = typeMeta.Icon;

  // Prefer the server's structured error message over a raw axios string.
  const readErr = (e, key, fallback) =>
    e.response?.data?.error ||
    e.response?.data?.message ||
    e.message ||
    t(key, fallback);

  const nameForUid = (channel, uid) => {
    const m = (channel.members || []).find((x) => String(x.user_id) === String(uid));
    return m?.user?.name || m?.user?.email || t('vaultShare.unknownRecipient', 'Unknown');
  };

  /** Encrypt to every member with a key (self always included) and send. */
  const deliver = async (channel, keysMap) => {
    const { publicJwk } = await ensureE2eKeypair();
    const recipientJwks = {};
    for (const [uid, jwk] of Object.entries(keysMap || {})) {
      if (jwk) recipientJwks[String(uid)] = jwk;
    }
    // Include self with the local (freshest) public key — the server map may
    // predate this session's lazy publish.
    recipientJwks[String(currentUserId)] = JSON.stringify(publicJwk);

    if (Object.keys(recipientJwks).length > MAX_RECIPIENTS) {
      throw new Error(
        t('vaultShare.tooManyRecipients', 'This channel is too large for encrypted shares')
      );
    }

    // AAD context: the channel this is actually being sent to and this account
    // as sender. Binding both means the server cannot relocate the ciphertext
    // or a per-recipient wrap to another channel, sender or recipient.
    const enc = await encryptForRecipients(
      { type: item.type, name: item.name, data: item.data },
      recipientJwks,
      { channelId: channel.id, senderUserId: currentUserId }
    );
    const metadata = {
      v: enc.v,
      alg: enc.alg,
      label: item.name,
      item_type: item.type,
      ct: enc.ct,
      iv: enc.iv,
      keys: enc.keys,
      expires_at: expiryHours
        ? new Date(Date.now() + expiryHours * 3600 * 1000).toISOString()
        : null,
    };

    // The server rejects metadata over 16 KB; a big item wrapped to many
    // recipients can exceed it even under MAX_RECIPIENTS. Block here with a
    // clear message rather than surfacing a raw 422.
    const metaBytes = new TextEncoder().encode(JSON.stringify(metadata)).length;
    if (metaBytes > MAX_METADATA_BYTES) {
      throw new Error(
        t(
          'vaultShare.metadataTooLarge',
          'This item is too large to share encrypted here. Try a smaller conversation or remove large fields (e.g. a long private key).'
        )
      );
    }

    await chatService.sendMessage(channel.id, {
      uid: newUid(),
      body: '',
      kind: 'secret',
      metadata,
    });

    const { name } = channelDisplay(channel, currentUserId);
    setPendingWarn(null);
    setSentTo({ channelId: channel.id, name });
    useNotificationStore
      .getState()
      .success(
        t('vaultShare.sharedTo', { defaultValue: 'Secret shared to {{name}}', name })
      );
  };

  /**
   * Trust-on-first-use gate before delivering. Three outcomes and three
   * branches — a share only goes out on an explicit VERIFIED. Anything else,
   * including an undefined/garbled result, stops here: "we could not check"
   * must never be spelled the same way as "nothing changed".
   */
  const deliverWithTofu = async (channel, keysMap) => {
    const result = await verifyRecipientKeys(keysMap, { excludeUserId: currentUserId });
    const status = result?.status;

    if (status === KEY_CHECK.CHANGED) {
      setPendingWarn(null);
      setPendingVerifyFail(null);
      const changed = result.changed || [];
      setPendingKeyChange({
        channel,
        keysMap,
        changedUserIds: changed,
        changedNames: changed.map((uid) => nameForUid(channel, uid)),
      });
      return;
    }

    if (status !== KEY_CHECK.VERIFIED) {
      setPendingWarn(null);
      setPendingKeyChange(null);
      setPendingVerifyFail({ channel, keysMap, reason: result?.reason || 'unknown' });
      return;
    }

    await deliver(channel, keysMap);
  };

  /** Key-availability gate before delivering to a channel. */
  const checkAndDeliver = async (channel) => {
    // Publish our own key first so the fetched map is as complete as possible.
    await ensureE2eKeypair();
    const keysMap = await getChannelE2eKeys(channel.id);
    const members = channel.members || [];
    const others = members.filter((m) => m.user_id !== currentUserId);
    const missing = others.filter((m) => !keysMap[String(m.user_id)]);

    if (others.length > 0 && missing.length === others.length) {
      // Nobody else can receive — block instead of sending a card only the
      // sender could ever open.
      setPendingWarn({
        channel,
        keysMap,
        missingNames: missing.map((m) => m.user?.name || m.user?.email || 'Unknown'),
        blocked: true,
      });
      return;
    }
    if (missing.length > 0) {
      setPendingWarn({
        channel,
        keysMap,
        missingNames: missing.map((m) => m.user?.name || m.user?.email || t('vaultShare.unknownRecipient', 'Unknown')),
        blocked: false,
      });
      return;
    }
    await deliverWithTofu(channel, keysMap);
  };

  const sendTo = async (channel) => {
    if (sending) return;
    setSending(true);
    setError('');
    try {
      await checkAndDeliver(channel);
    } catch (e) {
      setError(readErr(e, 'vaultShare.shareFailed', 'Failed to share secret'));
    } finally {
      setSending(false);
    }
  };

  const sendToUser = async (user) => {
    if (sending) return;
    setSending(true);
    setError('');
    try {
      const channel = await ensureDm(user.id);
      await checkAndDeliver(channel);
    } catch (e) {
      setError(readErr(e, 'vaultShare.dmFailed', 'Could not start conversation'));
    } finally {
      setSending(false);
    }
  };

  const shareAnyway = async () => {
    if (sending || !pendingWarn || pendingWarn.blocked) return;
    setSending(true);
    setError('');
    try {
      await deliverWithTofu(pendingWarn.channel, pendingWarn.keysMap);
    } catch (e) {
      setError(readErr(e, 'vaultShare.shareFailed', 'Failed to share secret'));
    } finally {
      setSending(false);
    }
  };

  // User explicitly trusts the changed recipient key(s), then we re-pin + send.
  // The re-pin must land BEFORE anything is sent: if we cannot record the
  // decision we have not actually changed what we trust, so we do not send.
  const confirmKeyChange = async () => {
    if (sending || !pendingKeyChange) return;
    setSending(true);
    setError('');
    try {
      await pinRecipientKeys(pendingKeyChange.keysMap, pendingKeyChange.changedUserIds);
    } catch (e) {
      setError(
        t(
          'vaultShare.pinFailed',
          'Could not record that you trust this key, so nothing was shared. Unlock your vault and try again.'
        )
      );
      setSending(false);
      return;
    }
    try {
      await deliver(pendingKeyChange.channel, pendingKeyChange.keysMap);
      setPendingKeyChange(null);
    } catch (e) {
      setError(readErr(e, 'vaultShare.shareFailed', 'Failed to share secret'));
    } finally {
      setSending(false);
    }
  };

  // "Could not verify" is retryable (unlock the vault, then try again) but
  // never overridable — there is deliberately no "share anyway" on this path.
  const retryVerification = async () => {
    if (sending || !pendingVerifyFail) return;
    const { channel, keysMap } = pendingVerifyFail;
    setSending(true);
    setError('');
    try {
      setPendingVerifyFail(null);
      await deliverWithTofu(channel, keysMap);
    } catch (e) {
      setError(readErr(e, 'vaultShare.shareFailed', 'Failed to share secret'));
    } finally {
      setSending(false);
    }
  };

  const openChat = () => {
    if (sentTo) {
      setActiveChannel(sentTo.channelId);
      setActiveTab('chat.panel');
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('vaultShare.title', 'Share secret in chat')}
      customIcon={<Lock size={22} className="text-blue-500" />}
    >
      <div className="space-y-3">
        {/* What's being shared */}
        <div className="rounded-lg border border-gray-200 dark:border-zinc-700 px-3 py-2">
          <div className="chat-share-req-row">
            <TypeIcon size={14} className="chat-share-card-typeicon" />
            <span className="chat-share-card-name">{item.name || 'Untitled'}</span>
          </div>
          <div className="text-xs text-gray-400 dark:text-zinc-400" style={{ marginTop: 4 }}>
            {t(typeMeta.labelKey)}
            {' · '}
            {t('vaultShare.e2eHint', "end-to-end encrypted to each recipient's published key")}
          </div>
        </div>

        {sentTo ? (
          <div className="space-y-3">
            <div className="vault-share-success">
              ✓ {t('vaultShare.sharedTo', { defaultValue: 'Secret shared to {{name}}', name: sentTo.name })}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={openChat}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                {t('vaultShare.openChat', 'Open chat')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="modern-button-secondary flex-1 justify-center"
              >
                {t('common.done', 'Done')}
              </button>
            </div>
          </div>
        ) : pendingVerifyFail ? (
          <div className="space-y-3">
            <div className="chat-secret-warnbox">
              <AlertTriangle size={14} className="chat-secret-warnbox-icon" />
              <div>
                <p className="chat-secret-warnbox-title">
                  {t('vaultShare.verifyFailedTitle', "Couldn't verify recipient keys")}
                </p>
                <p className="chat-secret-warnbox-hint">
                  {pendingVerifyFail.reason === 'vault_locked' ||
                  pendingVerifyFail.reason === 'vault_unavailable'
                    ? t(
                        'vaultShare.verifyFailedLocked',
                        'Your vault has to be unlocked to check that recipients’ keys have not changed. Nothing was shared.'
                      )
                    : t(
                        'vaultShare.verifyFailedBody',
                        'The record of the keys you trust could not be read, so we cannot tell whether a recipient’s key has changed. Nothing was shared.'
                      )}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={retryVerification}
                disabled={sending}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                {sending
                  ? t('vaultShare.sending', 'Sending…')
                  : t('vaultShare.verifyRetry', 'Try again')}
              </button>
              <button
                type="button"
                onClick={() => setPendingVerifyFail(null)}
                disabled={sending}
                className="modern-button-secondary flex-1 justify-center"
              >
                {t('common.back', 'Back')}
              </button>
            </div>
            {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
          </div>
        ) : pendingKeyChange ? (
          <div className="space-y-3">
            <div className="chat-secret-warnbox">
              <AlertTriangle size={14} className="chat-secret-warnbox-icon" />
              <div>
                <p className="chat-secret-warnbox-title">
                  {t('vaultShare.keyChangedTitle', 'Encryption key changed')}
                </p>
                <p className="chat-secret-warnbox-names">{pendingKeyChange.changedNames.join(', ')}</p>
                <p className="chat-secret-warnbox-hint">
                  {t(
                    'vaultShare.keyChangedBody',
                    "Their encryption key changed since you last shared. Only continue if you trust this change — otherwise verify with them before sharing."
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={confirmKeyChange}
                disabled={sending}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                {sending
                  ? t('vaultShare.sending', 'Sending…')
                  : t('vaultShare.keyChangedConfirm', 'Trust new key & share')}
              </button>
              <button
                type="button"
                onClick={() => setPendingKeyChange(null)}
                disabled={sending}
                className="modern-button-secondary flex-1 justify-center"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
            {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
          </div>
        ) : pendingWarn ? (
          <div className="space-y-3">
            <div className="chat-secret-warnbox">
              <AlertTriangle size={14} className="chat-secret-warnbox-icon" />
              <div>
                <p className="chat-secret-warnbox-title">
                  {pendingWarn.blocked
                    ? t('vaultShare.nobodyHasKey', 'Nobody in this conversation can receive encrypted secrets yet.')
                    : t('vaultShare.someMissingKey', "Some members can't receive this secret yet:")}
                </p>
                <p className="chat-secret-warnbox-names">{pendingWarn.missingNames.join(', ')}</p>
                <p className="chat-secret-warnbox-hint">
                  {t(
                    'vaultShare.missingKeyHint',
                    "They can't receive yet — they need to open their vault once."
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {!pendingWarn.blocked && (
                <button
                  type="button"
                  onClick={shareAnyway}
                  disabled={sending}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                  {sending
                    ? t('vaultShare.sending', 'Sending…')
                    : t('vaultShare.shareAnyway', 'Share anyway')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setPendingWarn(null)}
                disabled={sending}
                className="modern-button-secondary flex-1 justify-center"
              >
                {pendingWarn.blocked ? t('common.back', 'Back') : t('common.cancel', 'Cancel')}
              </button>
            </div>
            {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
          </div>
        ) : (
          <>
            {/* Expiry */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="text-xs font-medium text-gray-500 dark:text-zinc-400" style={{ flexShrink: 0 }}>
                {t('vaultShare.expiryLabel', 'Expiry')}
              </span>
              <CompactSelectPicker
                value={expiryHours}
                onChange={setExpiryHours}
                options={EXPIRY_OPTIONS}
                aria-label={t('vaultShare.expiryLabel', 'Expiry')}
              />
              <span
                className="text-xs text-gray-400 dark:text-zinc-400"
                style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}
              >
                <ShieldCheck size={12} style={{ color: '#16a34a', flexShrink: 0 }} />
                {t('vaultShare.e2eShort', 'E2E encrypted')}
              </span>
            </div>

            {/* Recipient search */}
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#9ca3af' }} />
              <input
                className="modal-input modal-input-no-focus-outline"
                style={{ paddingLeft: 32 }}
                placeholder={t(
                  'vaultShare.searchPlaceholder',
                  'Filter conversations, or enter a full email address…'
                )}
                value={query}
                autoFocus
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}

            <OsScroll style={{ maxHeight: 256 }}>
              <div style={{ margin: '0 -4px', padding: '0 4px' }}>
              {sending && (
                <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
                  <Loader size={14} className="animate-spin" />{' '}
                  {t('vaultShare.sending', 'Sending…')}
                </div>
              )}

              {!sending && (
                <>
                  {/* Exact-email match (new DM) */}
                  {results.map((u) => (
                    <div key={`u-${u.id}`} className="chat-user-result" onClick={() => sendToUser(u)}>
                      <UserAvatar className="chat-avatar chat-avatar--sm" userId={u.id} name={u.name} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>{u.email}</div>
                      </div>
                    </div>
                  ))}

                  {/* Existing conversations */}
                  {filteredChannels.map((c) => {
                    const { name, seed } = channelDisplay(c, currentUserId);
                    return (
                      <div key={`c-${c.id}`} className="chat-user-result" onClick={() => sendTo(c)}>
                        {c.kind === 'dm' ? (
                          <UserAvatar
                            className="chat-avatar chat-avatar--sm"
                            userId={dmOther(c, currentUserId)?.user_id}
                            name={name}
                          />
                        ) : (
                          <div className="chat-avatar chat-avatar--sm" style={{ background: avatarColor(seed) }}>
                            {c.kind === 'channel' ? <Hash size={14} /> : initials(name)}
                          </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{name}</div>
                          <div style={{ fontSize: 11, color: '#9ca3af' }}>
                            {c.kind === 'dm'
                              ? t('vaultShare.directMessage', 'Direct message')
                              : t('vaultShare.memberCount', {
                                  defaultValue: '{{count}} members',
                                  count: (c.members || []).length,
                                })}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {filteredChannels.length === 0 && results.length === 0 && (
                    <div className="py-3 text-sm text-gray-400 dark:text-zinc-400">
                      {isCompleteEmail(query)
                        ? t('vaultShare.noUserFound', 'No user found with that email address.')
                        : t(
                            'vaultShare.noConversation',
                            'No matching conversation — enter a full email address to reach someone new.'
                          )}
                    </div>
                  )}
                </>
              )}
              </div>
            </OsScroll>
          </>
        )}
      </div>
    </Modal>
  );
}
